import { useEffect, useState, useCallback } from "react";
import { adminApi, api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/RichTextEditor";
import { useSettings } from "@/lib/settings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Download, LogOut, Lock, ArrowUp, ArrowDown, Copy, Info, Database } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem("admin_pwd") || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("admin_pwd"));

  if (!authed) {
    return <Login onLogin={(pwd) => { setPassword(pwd); setAuthed(true); }} />;
  }
  return <AdminDashboard password={password} onLogout={() => { sessionStorage.removeItem("admin_pwd"); setAuthed(false); setPassword(""); }} />;
}

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password: pwd });
      sessionStorage.setItem("admin_pwd", pwd);
      onLogin(pwd);
      toast.success("Connecté");
    } catch {
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto pt-12" data-testid="admin-login">
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-[#0b3a73] flex items-center gap-2">
            <Lock className="w-5 h-5" /> Espace administrateur
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pwd">Mot de passe</Label>
              <Input
                id="pwd"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                data-testid="admin-password-input"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0b3a73] hover:bg-[#0a2f5c]"
              data-testid="admin-login-btn"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminDashboard({ password, onLogout }) {
  const client = adminApi(password);
  const { refresh: refreshSettings } = useSettings();
  const [days, setDays] = useState([]);
  const [activeDayId, setActiveDayId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [regs, setRegs] = useState([]);
  const [displayMode, setDisplayMode] = useState("tabs");
  const [pageTitle, setPageTitle] = useState("");
  const [pageSubtitle, setPageSubtitle] = useState("");
  const [backBtn, setBackBtn] = useState({ enabled: false, text: "Retour au site", url: "" });

  const refresh = useCallback(async () => {
    const { data: ds } = await api.get("/days");
    setDays(ds);
    const { data: st } = await api.get("/settings");
    setDisplayMode(st.display_mode || "tabs");
    setPageTitle(st.page_title || "Planning des bénévoles");
    setPageSubtitle(st.page_subtitle || "");
    setBackBtn({
      enabled: !!st.back_button_enabled,
      text: st.back_button_text || "Retour au site",
      url: st.back_button_url || "",
    });
    let day = activeDayId;
    if (!day && ds.length > 0) {
      day = ds[0].id;
      setActiveDayId(day);
    }
    if (day) {
      const { data: pl } = await api.get(`/planning/${day}`);
      setAreas(pl.areas);
      setTimeslots(pl.timeslots);
      const { data: sl } = await client.get(`/admin/slots?day_id=${day}`);
      setSlots(sl);
      const { data: rg } = await client.get(`/admin/registrations?day_id=${day}`);
      setRegs(rg);
    } else {
      setAreas([]); setTimeslots([]); setSlots([]); setRegs([]);
    }
  }, [activeDayId, client]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [activeDayId]);

  const handleErr = (err, fallback = "Erreur") => {
    if (err?.response?.status === 401) {
      toast.error("Session expirée");
      onLogout();
      return;
    }
    toast.error(err?.response?.data?.detail || fallback);
  };

  const updateDisplayMode = async (mode) => {
    try {
      await client.put("/admin/settings", { display_mode: mode });
      setDisplayMode(mode);
      toast.success("Mode d'affichage mis à jour");
    } catch (e) { handleErr(e); }
  };

  const updateDayNote = async (dayId, note) => {
    try {
      await client.patch(`/admin/days/${dayId}/note`, { note });
      toast.success("Commentaire enregistré");
      refresh();
    } catch (e) { handleErr(e); }
  };

  const updateAreaDescription = async (areaId, description) => {
    try {
      await client.patch(`/admin/areas/${areaId}/description`, { description });
      toast.success("Description enregistrée");
      refresh();
    } catch (e) { handleErr(e); }
  };

  const updatePageContent = async (payload) => {
    try {
      await client.put("/admin/settings", payload);
      toast.success("Contenu enregistré");
      await refreshSettings();
      refresh();
    } catch (e) { handleErr(e); }
  };

  // --- CRUD handlers ---
  const addDay = async (label) => {
    try { await client.post("/admin/days", { label, order: days.length }); toast.success("Jour ajouté"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const renameDay = async (id, label) => {
    try { const d = days.find(x => x.id === id); await client.put(`/admin/days/${id}`, { label, order: d.order }); toast.success("Mis à jour"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const deleteDay = async (id) => {
    try { await client.delete(`/admin/days/${id}`); toast.success("Jour supprimé"); if (activeDayId === id) setActiveDayId(null); refresh(); }
    catch (e) { handleErr(e); }
  };
  const duplicateDay = async (id, label) => {
    try {
      const { data } = await client.post(`/admin/days/${id}/duplicate`, { label });
      toast.success("Jour dupliqué");
      setActiveDayId(data.id);
      refresh();
    } catch (e) { handleErr(e); }
  };

  const addArea = async (name) => {
    try { await client.post("/admin/areas", { day_id: activeDayId, name, order: areas.length }); toast.success("Zone ajoutée"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const renameArea = async (id, name) => {
    try { const a = areas.find(x => x.id === id); await client.put(`/admin/areas/${id}`, { day_id: activeDayId, name, order: a.order }); toast.success("Mis à jour"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const deleteArea = async (id) => {
    try { await client.delete(`/admin/areas/${id}`); toast.success("Zone supprimée"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const moveArea = async (id, dir) => {
    const sorted = [...areas].sort((a,b) => a.order - b.order);
    const idx = sorted.findIndex(a => a.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    try {
      await Promise.all([
        client.put(`/admin/areas/${a.id}`, { day_id: activeDayId, name: a.name, order: b.order }),
        client.put(`/admin/areas/${b.id}`, { day_id: activeDayId, name: b.name, order: a.order }),
      ]);
      refresh();
    } catch (e) { handleErr(e); }
  };

  const addTs = async (label) => {
    try { await client.post("/admin/timeslots", { day_id: activeDayId, label, order: timeslots.length }); toast.success("Créneau ajouté"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const renameTs = async (id, label) => {
    try { const t = timeslots.find(x => x.id === id); await client.put(`/admin/timeslots/${id}`, { day_id: activeDayId, label, order: t.order }); toast.success("Mis à jour"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const deleteTs = async (id) => {
    try { await client.delete(`/admin/timeslots/${id}`); toast.success("Créneau supprimé"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const moveTs = async (id, dir) => {
    const sorted = [...timeslots].sort((a,b) => a.order - b.order);
    const idx = sorted.findIndex(t => t.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx], b = sorted[swap];
    try {
      await Promise.all([
        client.put(`/admin/timeslots/${a.id}`, { day_id: activeDayId, label: a.label, order: b.order }),
        client.put(`/admin/timeslots/${b.id}`, { day_id: activeDayId, label: b.label, order: a.order }),
      ]);
      refresh();
    } catch (e) { handleErr(e); }
  };

  const addSlot = async (area_id, timeslot_id) => {
    try { await client.post("/admin/slots", { day_id: activeDayId, area_id, timeslot_id }); refresh(); }
    catch (e) { handleErr(e); }
  };
  const toggleBlocked = async (slot_id, blocked) => {
    try { await client.patch(`/admin/slots/${slot_id}`, { blocked }); refresh(); }
    catch (e) { handleErr(e); }
  };
  const deleteSlot = async (slot_id) => {
    try { await client.delete(`/admin/slots/${slot_id}`); toast.success("Place supprimée"); refresh(); }
    catch (e) { handleErr(e); }
  };

  const deleteReg = async (reg_id) => {
    try { await client.delete(`/admin/registrations/${reg_id}`); toast.success("Inscription supprimée"); refresh(); }
    catch (e) { handleErr(e); }
  };
  const editReg = async (reg_id, data) => {
    try { await client.put(`/admin/registrations/${reg_id}`, data); toast.success("Mis à jour"); refresh(); }
    catch (e) { handleErr(e); }
  };

  const exportCsv = async () => {
    try {
      const res = await client.get(`/admin/export${activeDayId ? `?day_id=${activeDayId}` : ""}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "benevoles_fete_du_port.csv";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Export téléchargé");
    } catch (e) {
      console.error("Export error", e);
      handleErr(e, "Export impossible");
    }
  };

  const exportBackup = async () => {
    try {
      const res = await client.get("/admin/backup", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `backup_fete_du_port_${ts}.json`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Sauvegarde complète téléchargée");
    } catch (e) {
      console.error("Backup error", e);
      handleErr(e, "Sauvegarde impossible");
    }
  };

  const slotsFor = (area_id, ts_id) =>
    slots.filter(s => s.area_id === area_id && s.timeslot_id === ts_id).sort((a,b) => a.position - b.position);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#0b3a73]">Administration</h1>
        <Button variant="outline" onClick={onLogout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2"/>Déconnexion</Button>
      </div>

      <Tabs defaultValue="planning" className="w-full">
        <TabsList>
          <TabsTrigger value="planning" data-testid="tab-planning">Planning</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="tab-registrations">Inscriptions</TabsTrigger>
          <TabsTrigger value="structure" data-testid="tab-structure">Structure</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Contenu</TabsTrigger>
        </TabsList>

        {/* PLANNING TAB */}
        <TabsContent value="planning" className="space-y-4">
          <DaySelector days={days} activeDayId={activeDayId} setActiveDayId={setActiveDayId} />
          {activeDayId && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Places par cellule</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[760px]">
                  <thead>
                    <tr>
                      <th className="bg-[#0b3a73] text-white text-left px-3 py-2 text-sm">Zone</th>
                      {timeslots.map(ts => (
                        <th key={ts.id} className="bg-[#0b3a73] text-white px-3 py-2 text-sm text-center">{ts.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {areas.map(area => (
                      <tr key={area.id} className="border-t">
                        <th className="bg-[#cfe2f3] text-[#0b3a73] text-left px-3 py-2 text-sm align-top">{area.name}</th>
                        {timeslots.map(ts => (
                          <td key={ts.id} className="border-l align-top p-2 space-y-2 min-w-[160px]">
                            {slotsFor(area.id, ts.id).map(s => (
                              <div key={s.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 px-2 py-1 rounded border">
                                <div className="flex-1 truncate">
                                  {s.blocked ? <span className="text-red-700">Bloqué</span> : s.registration_id ? <span className="text-emerald-700">Pris</span> : <span className="text-slate-500">Libre</span>}
                                </div>
                                <Switch checked={s.blocked} onCheckedChange={(v) => toggleBlocked(s.id, v)} />
                                <ConfirmDeleteBtn
                                  title="Supprimer cette place ?"
                                  description={s.registration_id ? "Cette place est actuellement occupée. La supprimer effacera aussi l'inscription du bénévole." : "Cette place sera retirée du planning."}
                                  onConfirm={() => deleteSlot(s.id)}
                                  testid={`delete-slot-${s.id}`}
                                  iconSize="w-3.5 h-3.5"
                                />
                              </div>
                            ))}
                            <Button size="sm" variant="outline" className="w-full h-7 text-xs" onClick={() => addSlot(area.id, ts.id)} data-testid={`add-slot-${area.id}-${ts.id}`}>
                              <Plus className="w-3 h-3 mr-1"/>Place
                            </Button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* REGISTRATIONS TAB */}
        <TabsContent value="registrations" className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <DaySelector days={days} activeDayId={activeDayId} setActiveDayId={setActiveDayId} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportBackup} data-testid="export-backup-btn" title="Sauvegarde complète de toutes les collections (JSON)">
                <Database className="w-4 h-4 mr-2"/>Sauvegarde complète (JSON)
              </Button>
              <Button onClick={exportCsv} className="bg-[#0b3a73] hover:bg-[#0a2f5c]" data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2"/>Exporter CSV
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="registrations-table">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="text-left px-3 py-2">Prénom</th>
                    <th className="text-left px-3 py-2">Nom</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Téléphone</th>
                    <th className="text-left px-3 py-2">Zone</th>
                    <th className="text-left px-3 py-2">Créneau</th>
                    <th className="text-left px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-500">Aucune inscription.</td></tr>
                  )}
                  {regs.map(r => (
                    <tr key={r.id} className="border-t" data-testid={`reg-row-${r.id}`}>
                      <td className="px-3 py-2">{r.first_name}</td>
                      <td className="px-3 py-2">{r.last_name}</td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.phone}</td>
                      <td className="px-3 py-2">{r.area_name}</td>
                      <td className="px-3 py-2">{r.timeslot_label}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <EditRegBtn reg={r} onSave={(d) => editReg(r.id, d)} />
                          <ConfirmDeleteBtn
                            title="Supprimer cette inscription ?"
                            description={`Vous êtes sur le point de supprimer l'inscription de ${r.first_name} ${r.last_name} (${r.area_name} · ${r.timeslot_label}). Le créneau redeviendra disponible.`}
                            onConfirm={() => deleteReg(r.id)}
                            testid={`delete-reg-${r.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STRUCTURE TAB */}
        <TabsContent value="structure" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Affichage du planning public</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">
                Choisissez comment les différents jours sont présentés aux visiteurs sur la page d'accueil.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => updateDisplayMode("tabs")}
                  data-testid="display-mode-tabs"
                  className={`text-left border rounded-lg p-4 transition-colors ${displayMode === "tabs" ? "border-[#0b3a73] bg-blue-50 ring-2 ring-[#0b3a73]/20" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <div className="font-medium text-[#0b3a73]">Onglets (un jour à la fois)</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Les boutons en haut à droite changent le jour affiché. Une seule grille visible à la fois.
                  </div>
                </button>
                <button
                  onClick={() => updateDisplayMode("stacked")}
                  data-testid="display-mode-stacked"
                  className={`text-left border rounded-lg p-4 transition-colors ${displayMode === "stacked" ? "border-[#0b3a73] bg-blue-50 ring-2 ring-[#0b3a73]/20" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <div className="font-medium text-[#0b3a73]">Empilé (tous les jours sur une page)</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Toutes les grilles s'affichent l'une sous l'autre. Les boutons en haut à droite servent d'ancres pour défiler vers chaque jour.
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>
          <DaysManager days={days} addDay={addDay} renameDay={renameDay} deleteDay={deleteDay} duplicateDay={duplicateDay} setActiveDayId={setActiveDayId} activeDayId={activeDayId} />
          {activeDayId && (
            <div className="grid md:grid-cols-2 gap-4">
              <CrudList title="Zones" items={areas} onAdd={addArea} onRename={renameArea} onDelete={deleteArea} onMove={moveArea} onDescribe={updateAreaDescription} field="name" descriptionField="description" testid="areas" />
              <CrudList title="Créneaux horaires" items={timeslots} onAdd={addTs} onRename={renameTs} onDelete={deleteTs} onMove={moveTs} field="label" testid="timeslots" />
            </div>
          )}
          {days.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Commentaire sous le tableau (par jour)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-slate-600">
                  Ce texte s'affichera sous la grille du jour correspondant sur la page d'accueil. Laissez vide pour ne rien afficher.
                </p>
                {days.map((d) => (
                  <DayNoteEditor key={d.id} day={d} onSave={updateDayNote} />
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONTENT TAB */}
        <TabsContent value="content" className="space-y-4">
          <PageContentEditor
            initialTitle={pageTitle}
            initialSubtitle={pageSubtitle}
            onSave={updatePageContent}
          />
          <BackButtonEditor
            initial={backBtn}
            onSave={updatePageContent}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BackButtonEditor({ initial, onSave }) {
  const [enabled, setEnabled] = useState(!!initial?.enabled);
  const [text, setText] = useState(initial?.text || "Retour au site");
  const [url, setUrl] = useState(initial?.url || "");

  useEffect(() => { setEnabled(!!initial?.enabled); }, [initial?.enabled]);
  useEffect(() => { setText(initial?.text || "Retour au site"); }, [initial?.text]);
  useEffect(() => { setUrl(initial?.url || ""); }, [initial?.url]);

  const dirty =
    enabled !== !!initial?.enabled ||
    text !== (initial?.text || "Retour au site") ||
    url !== (initial?.url || "");

  const reset = () => {
    setEnabled(!!initial?.enabled);
    setText(initial?.text || "Retour au site");
    setUrl(initial?.url || "");
  };

  const validUrl = !url || /^https?:\/\//i.test(url);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Bouton de retour vers votre site</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Affiche un bouton en haut à gauche de la page publique pour permettre aux bénévoles de revenir vers votre site principal.
        </p>
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
          <div>
            <div className="text-sm font-medium">Activer le bouton</div>
            <div className="text-xs text-slate-500">Si désactivé, le bouton ne s'affichera pas.</div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="back-btn-enabled-switch"
          />
        </div>
        <div>
          <Label htmlFor="back-btn-text">Texte du bouton</Label>
          <Input
            id="back-btn-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Retour au site"
            data-testid="back-btn-text-input"
          />
        </div>
        <div>
          <Label htmlFor="back-btn-url">URL de destination</Label>
          <Input
            id="back-btn-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.monclub.ch"
            data-testid="back-btn-url-input"
          />
          {url && !validUrl && (
            <p className="text-xs text-red-600 mt-1">L'URL doit commencer par http:// ou https://</p>
          )}
          {!url && (
            <p className="text-xs text-slate-500 mt-1">Le bouton ne s'affichera pas tant que l'URL est vide.</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          {dirty && (
            <Button variant="outline" onClick={reset} data-testid="back-btn-reset">
              Annuler
            </Button>
          )}
          <Button
            onClick={() => onSave({
              back_button_enabled: enabled,
              back_button_text: text,
              back_button_url: url.trim(),
            })}
            disabled={!dirty || !validUrl}
            className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
            data-testid="back-btn-save"
          >
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PageContentEditor({ initialTitle, initialSubtitle, onSave }) {
  const [title, setTitle] = useState(initialTitle || "");
  const [subtitle, setSubtitle] = useState(initialSubtitle || "");

  useEffect(() => { setTitle(initialTitle || ""); }, [initialTitle]);
  useEffect(() => { setSubtitle(initialSubtitle || ""); }, [initialSubtitle]);

  const dirty = title !== (initialTitle || "") || subtitle !== (initialSubtitle || "");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Page d'accueil</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Personnalisez le titre et le sous-titre affichés en haut du planning public.
        </p>
        <div>
          <Label htmlFor="page-title">Titre principal</Label>
          <Input
            id="page-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Planning des bénévoles"
            data-testid="page-title-input"
          />
        </div>
        <div>
          <Label htmlFor="page-subtitle">Sous-titre / phrase d'introduction</Label>
          <Textarea
            id="page-subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            rows={3}
            placeholder="Inscrivez-vous en un clic à un créneau disponible..."
            data-testid="page-subtitle-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Laissez vide pour ne rien afficher en dessous du titre. Les sauts de ligne sont conservés.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          {dirty && (
            <Button
              variant="outline"
              onClick={() => { setTitle(initialTitle || ""); setSubtitle(initialSubtitle || ""); }}
              data-testid="page-content-reset"
            >
              Annuler
            </Button>
          )}
          <Button
            onClick={() => onSave({ page_title: title, page_subtitle: subtitle })}
            disabled={!dirty}
            className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
            data-testid="page-content-save"
          >
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DayNoteEditor({ day, onSave }) {
  const [val, setVal] = useState(day.note || "");
  const initial = day.note || "";
  const dirty = val !== initial;

  useEffect(() => { setVal(day.note || ""); }, [day.note]);

  return (
    <div className="space-y-2" data-testid={`day-note-${day.id}`}>
      <div className="flex items-center justify-between">
        <Label className="font-medium text-[#0b3a73]">{day.label}</Label>
        <div className="flex gap-2">
          {dirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVal(initial)}
              data-testid={`day-note-reset-${day.id}`}
            >
              Annuler
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onSave(day.id, val)}
            disabled={!dirty}
            className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
            data-testid={`day-note-save-${day.id}`}
          >
            Enregistrer
          </Button>
        </div>
      </div>
      <RichTextEditor
        value={val}
        onChange={setVal}
        placeholder="Aucun commentaire (laissez vide pour ne rien afficher)"
        testid={`day-note-editor-${day.id}`}
      />
    </div>
  );
}

function DaySelector({ days, activeDayId, setActiveDayId }) {
  return (
    <div className="flex items-center gap-3">
      <Label>Jour :</Label>
      <Select value={activeDayId || ""} onValueChange={setActiveDayId}>
        <SelectTrigger className="w-72" data-testid="admin-day-select"><SelectValue placeholder="Choisir un jour"/></SelectTrigger>
        <SelectContent>
          {days.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function DaysManager({ days, addDay, renameDay, deleteDay, duplicateDay, setActiveDayId, activeDayId }) {
  const [val, setVal] = useState("");
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Jours</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="ex: Samedi 23 août 2025" data-testid="new-day-input"/>
          <Button onClick={() => { if(val.trim()){ addDay(val.trim()); setVal(""); } }} className="bg-[#0b3a73] hover:bg-[#0a2f5c]" data-testid="add-day-btn">
            <Plus className="w-4 h-4 mr-1"/>Ajouter
          </Button>
        </div>
        <div className="space-y-1">
          {days.map(d => (
            <div key={d.id} className={`flex items-center justify-between px-3 py-2 rounded border ${activeDayId===d.id?'border-[#0b3a73] bg-blue-50':'border-slate-200'}`}>
              <button onClick={() => setActiveDayId(d.id)} className="text-sm text-left flex-1" data-testid={`select-day-${d.id}`}>{d.label}</button>
              <div className="flex gap-2 items-center">
                <DuplicateDayBtn day={d} onDuplicate={duplicateDay} />
                <InlineEdit value={d.label} onSave={(v) => renameDay(d.id, v)} />
                <ConfirmDeleteBtn
                  title="Supprimer ce jour ?"
                  description={`Toutes les zones, créneaux, places et inscriptions du jour « ${d.label} » seront définitivement supprimés.`}
                  onConfirm={() => deleteDay(d.id)}
                  testid={`delete-day-${d.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DuplicateDayBtn({ day, onDuplicate }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  useEffect(() => { if (open) setLabel(`${day.label} (copie)`); }, [open, day.label]);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-slate-600 hover:text-[#0b3a73]"
        data-testid={`duplicate-day-${day.id}`}
        title="Dupliquer ce jour"
      >
        <Copy className="w-4 h-4"/>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dupliquer le jour</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Crée une copie de « {day.label} » avec les mêmes zones, créneaux et places (sans les inscriptions).
          </p>
          <div>
            <Label htmlFor="dup-label">Nom du nouveau jour</Label>
            <Input id="dup-label" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="duplicate-day-label-input"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
              onClick={() => { if (label.trim()) { onDuplicate(day.id, label.trim()); setOpen(false); } }}
              data-testid="duplicate-day-confirm-btn"
            >
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CrudList({ title, items, onAdd, onRename, onDelete, onMove, onDescribe, field, descriptionField, testid }) {
  const [val, setVal] = useState("");
  const sorted = [...items].sort((a, b) => a.order - b.order);
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Nouveau ${title.toLowerCase()}`} data-testid={`new-${testid}-input`}/>
          <Button onClick={() => { if(val.trim()){ onAdd(val.trim()); setVal(""); } }} className="bg-[#0b3a73] hover:bg-[#0a2f5c]" data-testid={`add-${testid}-btn`}>
            <Plus className="w-4 h-4 mr-1"/>Ajouter
          </Button>
        </div>
        <div className="space-y-1">
          {sorted.map((it, idx) => (
            <div key={it.id} className="flex items-center justify-between px-3 py-2 rounded border border-slate-200">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{it[field]}</div>
                {descriptionField && it[descriptionField] && (
                  <div className="text-xs text-slate-500 truncate italic">{it[descriptionField]}</div>
                )}
              </div>
              <div className="flex gap-1 items-center">
                {onMove && (
                  <>
                    <button
                      onClick={() => onMove(it.id, -1)}
                      disabled={idx === 0}
                      className="text-slate-600 hover:text-[#0b3a73] disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      data-testid={`move-up-${testid}-${it.id}`}
                      title="Monter"
                    ><ArrowUp className="w-4 h-4"/></button>
                    <button
                      onClick={() => onMove(it.id, 1)}
                      disabled={idx === sorted.length - 1}
                      className="text-slate-600 hover:text-[#0b3a73] disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      data-testid={`move-down-${testid}-${it.id}`}
                      title="Descendre"
                    ><ArrowDown className="w-4 h-4"/></button>
                  </>
                )}
                {onDescribe && descriptionField && (
                  <DescribeBtn
                    item={it}
                    fieldName={field}
                    value={it[descriptionField] || ""}
                    onSave={(v) => onDescribe(it.id, v)}
                    testid={`describe-${testid}-${it.id}`}
                  />
                )}
                <InlineEdit value={it[field]} onSave={(v) => onRename(it.id, v)} />
                <ConfirmDeleteBtn
                  title={`Supprimer ${title === "Zones" ? "cette zone" : "ce créneau"} ?`}
                  description={`« ${it[field]} » sera supprimé${title === "Zones" ? "e" : ""} ainsi que toutes les places et inscriptions associées.`}
                  onConfirm={() => onDelete(it.id)}
                  testid={`delete-${testid}-${it.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DescribeBtn({ item, fieldName, value, onSave, testid }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => { if (open) setVal(value); }, [open, value]);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`p-1 ${value ? "text-[#0b3a73]" : "text-slate-400"} hover:text-[#0b3a73]`}
        data-testid={testid}
        title="Description (affichée au survol du ?)"
      >
        <Info className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Description de « {item[fieldName]} »</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Ce texte s'affichera dans une bulle d'aide quand le bénévole cliquera sur le « ? » à côté du nom de la zone.
          </p>
          <Textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            rows={5}
            placeholder="Ex : Service derrière le bar du club. Encaissement, distribution des boissons, nettoyage des verres."
            data-testid={`${testid}-textarea`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              className="bg-[#0b3a73] hover:bg-[#0a2f5c]"
              onClick={() => { onSave(val); setOpen(false); }}
              data-testid={`${testid}-save`}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InlineEdit({ value, onSave }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-slate-600 hover:text-[#0b3a73]"><Pencil className="w-4 h-4"/></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier</DialogTitle></DialogHeader>
          <Input value={v} onChange={(e) => setV(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="bg-[#0b3a73] hover:bg-[#0a2f5c]" onClick={() => { onSave(v); setOpen(false); }}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConfirmDeleteBtn({ title, description, onConfirm, testid, iconSize = "w-4 h-4" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          className="text-red-600 hover:text-red-800 p-1"
          data-testid={testid}
          title="Supprimer"
        >
          <Trash2 className={iconSize} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700"
            data-testid={`${testid}-confirm`}
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditRegBtn({ reg, onSave }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ first_name: reg.first_name, last_name: reg.last_name, email: reg.email, phone: reg.phone });
  useEffect(() => { if (open) setF({ first_name: reg.first_name, last_name: reg.last_name, email: reg.email, phone: reg.phone }); }, [open, reg]);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-slate-600 hover:text-[#0b3a73]" data-testid={`edit-reg-${reg.id}`}><Pencil className="w-4 h-4"/></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier l'inscription</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prénom</Label><Input value={f.first_name} onChange={(e) => setF({...f, first_name: e.target.value})}/></div>
              <div><Label>Nom</Label><Input value={f.last_name} onChange={(e) => setF({...f, last_name: e.target.value})}/></div>
            </div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({...f, email: e.target.value})}/></div>
            <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="bg-[#0b3a73] hover:bg-[#0a2f5c]" onClick={() => { onSave(f); setOpen(false); }}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
